import {
  FILE_UPLOAD_STATUS,
  FILTER_ALL_VALUE,
  UPLOAD_ACTION_OPTIONS,
} from "constants/constants";
import { itemMatchesSearchTerm } from "utils/searchUtils";

import {
  UPLOAD_HISTORY_COPY,
  UPLOAD_HISTORY_SEARCHABLE_FIELDS,
  UPLOAD_HISTORY_STATUS_FILTER,
  UPLOAD_HISTORY_STATUS_VARIANT,
  UPLOAD_QUERY_PARAMS,
} from "../constants/uploadsContent";

export const getUploadActionFromScriptId = (scriptIdParam) => {
  if (!scriptIdParam) {
    return {};
  }

  const scriptId = Number(scriptIdParam);
  return UPLOAD_ACTION_OPTIONS.find((option) => option.scriptId === scriptId) ?? {};
};

export const getScriptIdFromSearchParams = (searchParams) =>
  searchParams.get(UPLOAD_QUERY_PARAMS.SCRIPT_ID);

export const getUploadHistoryRowTimestamp = (row) =>
  row.created_at ?? row.createdAt ?? row.uploaded_at ?? null;

export const formatUploadDate = (dateValue) => {
  if (!dateValue) {
    return UPLOAD_HISTORY_COPY.DATE_FALLBACK;
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return UPLOAD_HISTORY_COPY.DATE_FALLBACK;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const getUploadHistoryStatusVariant = (row) => {
  const statusId = row.status?.id;
  const errorRows = Number(row.result?.error_rows ?? 0);
  const totalRows = Number(row.result?.total_rows ?? 0);

  if (statusId === FILE_UPLOAD_STATUS.PROCESSED) {
    if (totalRows > 0 && errorRows >= totalRows) {
      return UPLOAD_HISTORY_STATUS_VARIANT.FAILED;
    }

    if (errorRows > 0 && errorRows < totalRows) {
      return UPLOAD_HISTORY_STATUS_VARIANT.PARTIAL;
    }

    return UPLOAD_HISTORY_STATUS_VARIANT.PROCESSED;
  }

  if (statusId === FILE_UPLOAD_STATUS.UPLOADED) {
    return UPLOAD_HISTORY_STATUS_VARIANT.UPLOADED;
  }

  return UPLOAD_HISTORY_STATUS_VARIANT.PENDING;
};

export const getUploadHistoryStatusLabel = (row, variant) => {
  if (variant === UPLOAD_HISTORY_STATUS_VARIANT.FAILED) {
    return UPLOAD_HISTORY_COPY.STATUS_FAILED;
  }

  if (variant === UPLOAD_HISTORY_STATUS_VARIANT.PARTIAL) {
    return UPLOAD_HISTORY_COPY.STATUS_PARTIAL;
  }

  return row.status?.name || UPLOAD_HISTORY_COPY.STATUS_PENDING;
};

export const getUploadHistoryStatusSubtext = (variant, errorRows) => {
  if (variant === UPLOAD_HISTORY_STATUS_VARIANT.PROCESSED) {
    return UPLOAD_HISTORY_COPY.STATUS_COMPLETED;
  }

  if (variant === UPLOAD_HISTORY_STATUS_VARIANT.FAILED && errorRows > 0) {
    return UPLOAD_HISTORY_COPY.STATUS_ALL_FAILED;
  }

  if (variant === UPLOAD_HISTORY_STATUS_VARIANT.PARTIAL && errorRows > 0) {
    return UPLOAD_HISTORY_COPY.ROWS_FAILED(errorRows);
  }

  return null;
};

export const getUploadHistoryDownloadLabel = (isProcessed, isDownloading) => {
  if (isDownloading) {
    return UPLOAD_HISTORY_COPY.DOWNLOAD_LOADING;
  }

  if (isProcessed) {
    return UPLOAD_HISTORY_COPY.DOWNLOAD_BUTTON;
  }

  return UPLOAD_HISTORY_COPY.DOWNLOAD_UNAVAILABLE;
};

export const isUploadHistoryRowWithinDateRange = (row, dateRangeDays) => {
  if (dateRangeDays === null) {
    return true;
  }

  const uploadedAt = getUploadHistoryRowTimestamp(row);

  if (!uploadedAt) {
    return false;
  }

  const date = new Date(uploadedAt);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - dateRangeDays);

  return date >= cutoff;
};

export const matchesUploadHistoryStatusFilter = (row, statusFilter) => {
  if (statusFilter === FILTER_ALL_VALUE) {
    return true;
  }

  const variant = getUploadHistoryStatusVariant(row);

  if (statusFilter === UPLOAD_HISTORY_STATUS_FILTER.PROCESSED) {
    return variant === UPLOAD_HISTORY_STATUS_VARIANT.PROCESSED;
  }

  if (statusFilter === UPLOAD_HISTORY_STATUS_FILTER.FAILED) {
    return (
      variant === UPLOAD_HISTORY_STATUS_VARIANT.FAILED ||
      variant === UPLOAD_HISTORY_STATUS_VARIANT.PARTIAL
    );
  }

  if (statusFilter === UPLOAD_HISTORY_STATUS_FILTER.UPLOADED) {
    return variant === UPLOAD_HISTORY_STATUS_VARIANT.UPLOADED;
  }

  return false;
};

export const filterUploadHistoryRows = ({
  uploadHistory,
  scriptId,
  searchTerm,
  statusFilter,
  dateRangeDays,
}) => {
  if (!Array.isArray(uploadHistory)) {
    return [];
  }

  const normalizedSearchQuery = searchTerm.trim().toLowerCase();

  return uploadHistory
    .filter((row) => Number(row?.script?.id) === Number(scriptId))
    .filter((row) => isUploadHistoryRowWithinDateRange(row, dateRangeDays))
    .filter((row) =>
      itemMatchesSearchTerm(
        row,
        normalizedSearchQuery,
        UPLOAD_HISTORY_SEARCHABLE_FIELDS
      )
    )
    .filter((row) => matchesUploadHistoryStatusFilter(row, statusFilter))
    .sort(
      (a, b) =>
        new Date(getUploadHistoryRowTimestamp(b)) -
        new Date(getUploadHistoryRowTimestamp(a))
    );
};
